/* eslint-disable react/prop-types */
import moment from 'moment';
import React from 'react';
import { useTheme } from 'styled-components';
import { tokens, orange, inkA } from '../tokens';

// Gen-3 desktop date cell for event table rows: a thin vertical accent bar, a
// bold month/day line, and a small weekday chip + year underneath. Multi-day
// aware. Derives its dates from an event the same way `eventDateCardProps`
// does (start_date/end_date in MM-DD-YYYY, plus the isMultiDay flag), so both
// work from the same GraphQL row. Pass `muted` for past events to render the
// accent bar and weekday chip in neutral ink tones instead of brand orange.

const parse = (d) => moment(d, 'MM-DD-YYYY');

const EventDateCell = ({ event, muted = false }) => {
  const theme = useTheme();
  const t = tokens(theme);

  if (!event) return null;

  const start = parse(event.start_date);

  // Fallback when start_date is missing/invalid — mirror eventDateCardProps by
  // leaning on the pre-derived display fields the resolver already provides.
  if (!start.isValid()) {
    const main = [event.month, event.date || event.day]
      .filter(Boolean)
      .join(' ');
    return (
      <div style={wrap}>
        <span style={barStyle(t, muted)} />
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
          <div style={mainStyle(t)}>{main || event.date || ''}</div>
          {event.year ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 3,
              }}
            >
              <span style={yearStyle(t)}>{event.year}</span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  const end = event.end_date ? parse(event.end_date) : null;
  const multi =
    event.isMultiDay &&
    end &&
    end.isValid() &&
    end.format('MM-DD-YYYY') !== start.format('MM-DD-YYYY');

  const md = (d) => d.format('MMM D');
  const wk = (d) => d.format('ddd').toUpperCase();
  const sameMonth =
    multi && start.month() === end.month() && start.year() === end.year();

  const mainDate = multi
    ? sameMonth
      ? `${md(start)}–${end.format('D')}`
      : `${md(start)} – ${md(end)}`
    : md(start);
  const wkLabel = multi ? `${wk(start)}–${wk(end)}` : wk(start);
  const yr =
    String(start.year()) +
    (multi && end.year() !== start.year() ? `–${end.year()}` : '');

  return (
    <div style={wrap}>
      <span style={barStyle(t, muted)} />
      <div style={{ minWidth: 0, overflow: 'hidden' }}>
        <div style={mainStyle(t)}>{mainDate}</div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 3,
          }}
        >
          <span style={chipStyle(theme, muted)}>{wkLabel}</span>
          <span style={yearStyle(t)}>{yr}</span>
        </div>
      </div>
    </div>
  );
};

const wrap = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
  overflow: 'hidden',
};

const barStyle = (t, muted) => ({
  flexShrink: 0,
  width: 4,
  alignSelf: 'stretch',
  minHeight: 30,
  borderRadius: 3,
  background: muted ? t.faint : orange(0.85),
});

const mainStyle = (t) => ({
  fontSize: '15px',
  fontWeight: 800,
  color: t.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  letterSpacing: '-0.2px',
});

const chipStyle = (theme, muted) => ({
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.6px',
  color: muted ? inkA(theme, 0.62) : orange(1),
  background: muted ? inkA(theme, 0.05) : orange(0.1),
  border: `1px solid ${muted ? inkA(theme, 0.14) : orange(0.32)}`,
  borderRadius: 5,
  padding: '1px 6px',
  whiteSpace: 'nowrap',
});

const yearStyle = (t) => ({
  fontSize: '11px',
  fontWeight: 600,
  color: t.faint,
  whiteSpace: 'nowrap',
});

export default EventDateCell;
